import { Box, Button, Flex, Heading, VStack } from '@chakra-ui/react';
import { Image } from '@common/components/Image';
import { FontFamily } from '@common/theming/Fonts';
import tracking from '@common/tracking';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { TextBlock } from '../../common/components/TextBlock';
import { ROUTES } from '../../config';
import { useWaitlistViewModel } from '../models/WaitlistViewModel';
import { CheckStatusForm } from './components/CheckStatusForm';
import { Footer } from './components/Footer';
import { JoinForm } from './components/JoinForm';

export const WaitListPage: React.FC = () => {
  const [formState, setFormState] = useState<'JOIN' | 'CHECK'>('JOIN');
  const joinForm = useForm<{ email: string }>();
  const checkForm = useForm<{ email: string; code: string }>();
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const model = useWaitlistViewModel();
  const [params] = useSearchParams();

  const onJoinFormSubmit = joinForm.handleSubmit(async (data) => {
    setIsLoading(true);
    const referralCode = params.get('referralCode') ?? undefined;
    const result = await model.joinWaitlist(data.email, referralCode);
    if (result.error) {
      joinForm.setError('email', { message: result.error });
      setIsLoading(false);
    } else {
      tracking.track({ action: 'Waitlist Joined', email: data.email });
      navigate(ROUTES.waitlistStatus + `?code=${result.code}&position=${result.position}`);
      setIsLoading(false);
    }
  });

  const onCheckFormSubmit = checkForm.handleSubmit(async (data) => {
    setIsLoading(true);
    const result = data.code ? await model.checkStatusByCode(data.code) : await model.checkStatusByEmail(data.email);
    if (result.error) {
      checkForm.setError('code', { message: result.error });
      setIsLoading(false);
    } else {
      navigate(ROUTES.waitlistStatus + `?code=${result.code}&position=${result.position}`);
      setIsLoading(false);
    }
  });

  const autoJoin = async () => {
    const email = params.get('email');
    if (email && !joinForm.getValues('email')) {
      joinForm.setValue('email', email ?? '');
      await onJoinFormSubmit();
    }
  };

  useEffect(() => {
    autoJoin();
  }, []);

  return (
    <VStack bg="black" minH={'100vh'} px={'40px'}>
      {/* Header */}
      <Flex width="full" direction="row" height={[70, 90]} color="white" alignItems={'center'} zIndex={'3'}>
        <Image image="logo-white" w={[100, 160]} margin={'auto auto'} />
      </Flex>

      {/* Graphic */}
      <Box maxW={[730]} px={['30px', '100px']} width={'100%'} height={'100%'} zIndex={'2'} position={'relative'}>
        <Box
          px={['30px', '100px']}
          position={'absolute'}
          background={'linear-gradient(360deg, rgba(0,0,0,0) 0%, rgba(0,0,0,1) 100%)'}
          maxWidth={[670, 530]}
          width={'100%'}
          height={'100%'}
          zIndex={'2'}
          mt={'-50px'}
          mb={'-100px'}></Box>
        <Image image="dashboard-header" mt={'-50px'} mb={'-100px'} zIndex={'1'} position={'relative'} />
      </Box>

      {/* Body */}
      <VStack
        bgColor={'white'}
        borderRadius={'10px'}
        maxW={[730]}
        padding={['30px', '60px']}
        zIndex={'3'}
        width={'100%'}>
        {/* Early Access Faux-Button */}
        <Box
          p="2px"
          display="inline-block"
          background={'linear-gradient(to bottom, #63AAFF, #C7E0FF, #FFD698, #C4A8FF)'}
          borderRadius={'8px'}
          borderColor={'white'}>
          <Box bgColor={'white'} borderRadius="6px" px="20px">
            <TextBlock weight="bold" fontSize={'12px'} lineHeight="30px">
              Get Early Access!
            </TextBlock>
          </Box>
        </Box>

        {/* Title */}
        <Box>
          <Heading
            color="gray.900"
            textAlign={'center'}
            fontFamily={FontFamily.DM_SERIF_DISPLAY_REGULAR}
            fontSize={['3xl', '6xl']}
            fontWeight={'normal'}
            m={'30px auto '}
            lineHeight={'100%'}>
            Buy shares of
            <br /> rental properties
          </Heading>
        </Box>

        {/* Subtitle */}
        <Box>
          <TextBlock textAlign={'center'} color="gray.900" fontSize={'20px'} lineHeight={'120%'}>
            Buy fractions of investment homes starting at $100,
            <br /> and let us manage the headaches for you.
          </TextBlock>
        </Box>

        {/* Form */}
        {formState === 'JOIN' ? (
          <JoinForm form={joinForm} isLoading={isLoading} onSubmit={onJoinFormSubmit} />
        ) : (
          <CheckStatusForm form={checkForm} isLoading={isLoading} onSubmit={onCheckFormSubmit} />
        )}

        {/* Waitlist Check */}
        {formState === 'JOIN' ? (
          <Flex flexDirection={['column', 'row']} gap="5px">
            <TextBlock>Already on the waitlist?</TextBlock>
            <Button variant="link" p={0} m={0} onClick={() => setFormState('CHECK')}>
              View your status here
            </Button>
          </Flex>
        ) : (
          <Flex flexDirection={['column', 'row']} gap="5px">
            <TextBlock>Want to join the waitlist?</TextBlock>
            <Button variant="link" p={0} m={0} onClick={() => setFormState('JOIN')}>
              Click here to join
            </Button>
          </Flex>
        )}
      </VStack>

      {/* Footer */}
      <Footer />
    </VStack>
  );
};
